import React from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtCAD, fmtDate } from "@/lib/billing";

/* Shown instead of page content when a member has never activated, or their paid period has ended. */
export default function ActivationWall({ billing }) {
  const ended = billing?.status === "ended";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-8 sm:p-12 max-w-2xl" data-testid="activation-wall">
      <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ background: "hsl(var(--brand))" }}>
        <Lock className="h-5 w-5 text-white" />
      </div>
      <p className="mt-5 text-xs uppercase tracking-wider text-slate-500 font-medium" data-testid="activation-wall-kicker">
        {ended ? "Limited access" : "Activate your account"}
      </p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-display mt-1" data-testid="activation-wall-title">
        {ended
          ? `Your subscription ended on ${fmtDate(billing?.ended_at || billing?.period_end)}`
          : `Pay to activate — ${fmtCAD(billing?.plan_price_cad || 50)}/month + tax`}
      </h1>
      <p className="mt-3 text-sm text-slate-600 max-w-prose" data-testid="activation-wall-copy">
        {ended
          ? "Your realtors, notes and history are safe — reactivate to pick up where you left off. In the meantime you can still open Billing & Credits for your full payment history and receipts, and update your profile in Settings."
          : "Your plan keeps the CRM running and includes 1,500 enrichment + 600 scrape credits every month. Checkout happens on Authorize.Net's secure page — card details never touch this app. Nothing is charged until you confirm there."}
      </p>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
        {ended ? (
          <>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Reactivating restarts your monthly plan from today with a fresh {fmtCAD(billing?.plan_price_cad || 50)} charge plus tax — your purchased credits are still there.</li>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Imports, scans, enrichment and your realtor list unlock the moment payment is confirmed.</li>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Nothing is deleted while your account is in limited access.</li>
          </>
        ) : (
          <>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Realtor.ca imports, territory imports, open-house scans and enrichment unlock the moment payment is confirmed.</li>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Building a brand-new 900-realtor list needs ≈ $80 of extra credit packs in month one — the Billing page spells it out.</li>
            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> Cancel any time; access runs to the end of the paid month and your data is never deleted.</li>
          </>
        )}
      </ul>
      <Link to="/billing">
        <Button className="mt-6 text-white min-h-[44px]" style={{ background: "hsl(var(--brand))" }} data-testid="activation-wall-go">
          {ended ? "Reactivate on Billing & Credits" : "Go to Billing & Credits"}
        </Button>
      </Link>
    </div>
  );
}
